<template>
  <aside id="partner-faq" class="">
    <h2 class="ds-heading-small ds-margin-top-24 ds-margin-bottom-12">
      {{ metadata.partnerName }}
    </h2>

    <div class="ds-display-flex ds-align-items-center ds-margin-top-24">
      <DsIcon category="interface" name="location" class="ds-margin-right-12" /> <span>{{ metadata.location.name
        }}</span>
    </div>
    <div v-if="languages" class="ds-display-flex ds-align-items-center ds-margin-top-24">
      <DsIcon category="team" name="partnerships-team" class="ds-margin-right-12" /> <span>{{ languages }}</span>
    </div>
    <div class="ds-display-flex ds-align-items-center ds-margin-top-24">
      <DsIcon category="arrows" name="external-link" class="ds-margin-right-12" /> <a rel="noreferrer noopener"
        :href="metadata.url" target="_blank">{{ metadata.url }}</a>
    </div>

    <div class="ds-width-full ds-margin-y-48">
      <DsDivider />
    </div>
    <DsAccordion v-if="blocks.length" class="ds-background-color-grey-100 ds-padding-48">
      <DsAccordionItem v-for="(block) in blocks" :key="block.id" :title="block.name" :opened="false">
        <template #title>
          <div class="ds-display-flex ds-align-items-center ds-margin-bottom-12">
            <DsIcon :category="block.icon.category" :name="block.icon.name" class="ds-margin-right-12" /> <span
              class="ds-font-weight-semi-bold">{{ block.label }}</span>
          </div>
        </template>
        <div v-for="item in block.items" :key="item.id">
          <template v-if="item.link">
            <DsLink type="link" variant="tertiary" color="black" :content="item.link.text" :url="item.link.url" />
          </template>
          <template v-else>
            <div style="padding: 2px;">
              {{ item.name }}
            </div>
          </template>
        </div>
      </DsAccordionItem>
    </DsAccordion>
  </aside>
</template>

<script setup>
import { DsAccordion, DsAccordionItem, DsDivider, DsIcon, DsLink } from 'awds'

const { labels } = useGlobalData()
const store = useRoutesStore()
const { locale } = useI18n()

store.retrieveRoutes()
const props = defineProps({
  metadata: {
    type: Object,
    required: true
  }
})


const languages = computed(() => {
  return props.metadata.languages.map(l => l.name).join(', ')
})
const blocks = computed(() => {
  const { getInternalUrl } = useRoutes(store.routes, locale)

  return ['availability', 'industries', 'partners', 'products', 'paymentMethods', 'terminals']
    .map((section) => {
      const items = props.metadata[section] || null

      if (!(items?.length)) {
        return null
      }

      return {
        id: section,
        icon: getIconForSection(section),
        label: getLabelForSection(section),
        items: items
          .map((i) => {
            let link = null
            if (i.id && i.link) {
              link = i.link
            } else if (i.id && getInternalUrl(i.id)) {
              link = {
                text: i.name || i,
                title: i.name || i,
                url: getInternalUrl(i.id),
                nuxt: true,
                openInNewWindow: false
              }
            }
            return {
              id: i.id,
              name: i.name || i,
              link
            }
          }).sort((a, b) => (typeof a.name?.localeCompare === 'function') ? a.name.localeCompare(b.name) : 1)
      }
    })
    .filter(Boolean)
})

function getLabelForSection(section) {
  switch (section) {
    case 'availability':
      return labels.value.availableIn
    case 'industries':
      return labels.value.industry
    case 'partners':
      return labels.value.techPartners
    case 'products':
      return labels.value.adyenProduct
    case 'paymentMethods':
      return labels.value.paymentMethods
    case 'terminals':
      return labels.value.terminalsSupported
    default:
      return '-'
  }
}
function getIconForSection(section) {
  switch (section) {
    case 'availability':
      return {
        category: 'various',
        name: 'globe'
      }
    case 'industries':
      return {
        category: 'products-and-features',
        name: 'accounts'
      }
    case 'partners':
      return {
        category: 'products-24',
        name: 'adyen-giving'
      }
    case 'products':
      return {
        category: 'various',
        name: 'scalability'
      }
    case 'paymentMethods':
      return {
        category: 'products-24',
        name: 'payment-method-types'
      }
    case 'terminals':
      return {
        category: 'devices-24',
        name: 'terminal-3'
      }
    default:
      return {
        category: 'various',
        name: 'globe'
      }
  }
}

</script>
